import {
  FormApproveRejectRequest,
  FormApproveRejectResponse,
  HrmRequestFormResponse,
  RequestCountResponse,
} from '@Types/api/form';
import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { createApi } from '@reduxjs/toolkit/query/react';
import { RequestsStatusesResponse } from '@Types/api/dashboardWidgets/RequestsStatuses';
import { ApprovalHistory } from '@Types/api/requestList';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import { approvalsListApi } from './approvalsListApiSlice';

export const basicHrmRequestApi = createApi({
  reducerPath: ApiReducerPath.API_BASIC_HRM_REQUEST,
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    cancelHrmRequest: builder.mutation<null, string>({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/hrm-request/cancel`,
        method: 'post',
        params: { requestId },
      }),
    }),
    deleteHrmRequest: builder.mutation<null, string>({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/hrm-request`,
        method: 'delete',
        params: { requestId },
      }),
    }),
    getHrmRequestForm: builder.query<HrmRequestFormResponse, string>({
      query: (id) => ({
        url: `${ApiBaseName.ESS}/hrm-request`,
        method: 'get',
        params: { hrmRequestId: id },
      }),
    }),
    submitHrmRequest: builder.mutation<string, string>({
      query: (requestId) => ({
        url: `${ApiBaseName.ESS}/hrm-request/submit`,
        method: 'post',
        params: { requestId },
      }),
    }),
    respondHrmRequest: builder.mutation<
      FormApproveRejectResponse,
      FormApproveRejectRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/hrm-request/command`,
        method: 'post',
        body,
      }),
      onQueryStarted: (_, api) => {
        api.queryFulfilled.then(() => {
          api.dispatch(
            approvalsListApi.util.invalidateTags(['taskList', 'widgetTaskList'])
          );
        });
      },
    }),
    getRequestsCount: builder.mutation<RequestCountResponse, void>({
      query: () => ({
        url: `${ApiBaseName.ESS}/stats/request-count`,
        method: 'get',
      }),
    }),
    getRequestsStatuses: builder.mutation<RequestsStatusesResponse, void>({
      query: () => ({
        url: `${ApiBaseName.ESS}/stats/request-by-statuses`,
        method: 'get',
      }),
    }),
    getRequisitionRequestsCount: builder.mutation<RequestCountResponse, void>({
      query: () => ({
        url: `${ApiBaseName.REQUISITION}/requisition/stats/request-count`,
        method: 'get',
      }),
    }),
    getRequisitionRequestsStatuses: builder.mutation<
      RequestsStatusesResponse,
      void
    >({
      query: () => ({
        url: `${ApiBaseName.REQUISITION}/requisition/stats/request-by-statuses`,
        method: 'get',
      }),
    }),
    getApprovalHistory: builder.mutation<ApprovalHistory[], string>({
      query: (id) => ({
        url: `${ApiBaseName.ESS}/hrm-request/${id}/history`,
        method: 'get',
      }),
    }),
  }),
});

export const {
  useGetRequestsStatusesMutation,
  useGetRequestsCountMutation,
  useGetRequisitionRequestsCountMutation,
  useGetRequisitionRequestsStatusesMutation,
  useDeleteHrmRequestMutation,
  useGetHrmRequestFormQuery,
  useSubmitHrmRequestMutation,
  useRespondHrmRequestMutation,
  useCancelHrmRequestMutation,
  useGetApprovalHistoryMutation,
} = basicHrmRequestApi;
